import React, { useState, useEffect } from 'react';
import doctorProfileImage from '../../assets/doctorprofile.png';
import { useNavigate } from 'react-router';

const DoctorProfile = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M40 40C50 20 70 10 90 20C110 30 130 10 150 30C170 50 190 80 170 100C150 120 170 140 150 160C130 180 100 170 80 150C60 130 40 150 20 130C0 110 20 80 40 60C60 40 30 60 40 40Z" fill="#00B2C2" />
        </svg>
      </div>
      
      <div className="absolute bottom-0 left-0 w-48 h-48 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M40 40C60 20 90 30 110 50C130 70 160 60 170 90C180 120 160 150 130 160C100 170 70 150 60 120C50 90 20 80 10 50C0 20 20 60 40 40Z" fill="#A6CE39" />
        </svg>
      </div>
      
      <div className="flex flex-col md:flex-row gap-8 lg:gap-12 relative">
        {/* Left column with image */}
        <div className={`md:w-2/5 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-lime-400 rounded-full opacity-50"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-teal-500 rounded-full opacity-50"></div>
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <img 
                src={doctorProfileImage}
                alt="Dr. Aesha Thakkar"
                className="w-full h-full object-cover transform transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>
          
          {/* Profile badge */}
          <div className="bg-white shadow-lg rounded-lg p-4 w-3/4 mx-auto -mt-10 relative z-10 border-l-4 border-teal-500">
            <div className="flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="text-teal-500 mr-2">
                <path d="M50 20C60 30 70 15 80 25C90 35 75 50 85 60C95 70 80 85 70 75C60 65 50 80 40 70C30 60 45 45 35 35C25 25 40 10 50 20Z" fill="currentColor" />
              </svg>
              <span className="font-semibold text-gray-800">BDS, Dentist</span>
            </div>
          </div>
        </div>

        {/* Right column with content */}
        <div className={`md:w-3/5 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Logo */}
          <div className="w-16 h-16 mb-6 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-full shadow-md">
            <svg width="32" height="32" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 20C60 30 70 15 80 25C90 35 75 50 85 60C95 70 80 85 70 75C60 65 50 80 40 70C30 60 45 45 35 35C25 25 40 10 50 20Z" fill="white" />
            </svg>
          </div>

          {/* Name and content */}
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
            Dr. Aesha Thakkar
            <div className="h-1 w-20 bg-lime-400 mt-2"></div>
          </h1>

          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-teal-600">Why dentistry?</h2>
            <p className="text-gray-700 mb-6 text-base md:text-lg">
              I didn’t always know I’d become a dentist — I just knew I wanted to work closely with people, make a difference in their lives, and never stop learning. Over time, dentistry checked all those boxes — and then some. It gives me the perfect balance of science, art, and human connection.
            </p>
            <p className="text-lg text-gray-700">
              What truly drew me in was the ability to solve real problems and help people feel confident in their own smiles. Whether it’s easing dental anxiety, restoring someone’s ability to eat comfortably, or simply helping them feel seen and cared for — that’s what I love most about what I do.
            </p>
          </div>

          {/* Education section */}
          <div className="mb-8 pl-4 border-l-2 border-teal-300">
            <h2 className="text-xl font-semibold mb-4 text-teal-600">Education</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 mt-2 rounded-full bg-lime-400 mr-2"></span>
                <span>Bachelor of Dental Surgery, CDSH, Rau, Indore</span>
              </li>
            </ul>
          </div>

          {/* Activities section */}
          <div className="mb-8 pl-4 border-l-2 border-teal-300">
            <h2 className="text-xl font-semibold mb-4 text-teal-600">Out and About in Vadodara</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 mt-2 rounded-full bg-lime-400 mr-2"></span>
                <span>Walks with her dog in Sayaji Baug</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 mt-2 rounded-full bg-lime-400 mr-2"></span>
                <span>Exploring the food scene — from street chaats to boutique cafes</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 mt-2 rounded-full bg-lime-400 mr-2"></span>
                <span>Movie nights with a good rom-com or mystery thriller</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 mt-2 rounded-full bg-lime-400 mr-2"></span>
                <span>Weekend trips to nearby places</span>
              </li>
            </ul>
          </div>

          {/* CTA Button */}
          <button 
              onClick={() => navigate('/dr-aesha')} 
              className="cursor-pointer bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-3 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform transition-all duration-300 hover:-translate-y-1 flex items-center">
            LEARN MORE
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-2">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfile;